import { useState, useEffect } from 'react';
import { fetchRelatedVideos } from '../../services/videoApi';
import type { Video } from '../../services/videoApi/videoApi.types';
import { VideoData } from '../VideoCardSide/types';
import { RelatedVideosListProps, UseRelatedVideosListReturn } from './types';

/**
 * VideoDataの型にVideoを変換するヘルパー関数
 * @param video バックエンドから返されるVideo型
 * @returns VideoData型に変換されたデータ
 */
const mapVideoToVideoData = (video: Video): VideoData => {
  return {
    id: video.id,
    title: video.title,
    name: video.name,
    thumbnail: video.thumbnail || video.thumbnail_url,
    duration: video.duration,
    uploader: video.uploader ? {
      id: video.uploader.id,
      name: video.uploader.name,
      icon_path: video.uploader.icon_path,
      hospital: video.uploader.hospital ? {
        name: video.uploader.hospital.name
      } : undefined
    } : undefined,
    uploader_id: video.uploader_id,
    created_at: video.created_at,
    published_at: video.published_at,
    visibility: video.visibility === 'public' || video.visibility === 'private' || video.visibility === 'hospital_only'
      ? video.visibility
      : undefined,
    images: video.images,
    // 「資料 N」バッジ用
    materials: video.materials,
    // 変換中でサムネイル未設定の動画がスピナーではなく既定画像になってしまわないよう引き継ぐ
    is_processing: video.is_processing,
    conversion_status: video.conversion_status,
  };
};

/**
 * 関連動画リストのビジネスロジックを扱うカスタムフック
 * @param video - 現在表示中の動画データ
 * @returns 関連動画リストと読み込み状態
 */
export const useRelatedVideosList = ({ video }: RelatedVideosListProps): UseRelatedVideosListReturn => {
  const [relatedVideos, setRelatedVideos] = useState<VideoData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // 関連動画のロード処理
  useEffect(() => {
    if (!video || !video.id) {
      return;
    }

    const loadRelatedVideos = async () => {
      setLoading(true);
      try {
        // videoデータを適合するように変換
        const videoArg: Video = {
          id: video.id,
          title: video.title,
          uploader: video.uploader ? {
            id: video.uploader.id || '', // 必須フィールドなので空文字を設定
            name: video.uploader.name || '', // 必須フィールドなので空文字を設定
            icon_path: video.uploader.icon_path
          } : undefined
        };
        
        // fetchRelatedVideos はサーバーから { videos: [ ... ] } の形を受け取る想定
        const response = await fetchRelatedVideos(videoArg);
        
        // 配列部分だけを state に格納（型変換を行う）
        setRelatedVideos((response.videos || []).map(mapVideoToVideoData));
      } catch (error) {
        console.error('関連動画取得エラー:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRelatedVideos();
  }, [video?.id]);

  return {
    relatedVideos,
    loading
  };
}; 